import React, { useState, useEffect, useRef } from 'react';

const Knob = ({
  label,
  value,
  min = 0,
  max = 100,
  onChange,
  size = 60,
  step = 1
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const startY = useRef(null);
  const startValue = useRef(null);

  // Normalize value to 0-1 range for drawing
  const normalizedValue = (value - min) / (max - min);

  // SVG Calculations
  const strokeWidth = 4;
  const radius = (size / 2) - strokeWidth;
  const center = size / 2;
  const circumference = 2 * Math.PI * radius;
  // Arc length (270 degrees = 0.75)
  const arcLength = circumference * 0.75;
  const offset = circumference - (normalizedValue * arcLength);
  const rotationOffset = 135; // Start at bottom left

  const handleMouseDown = (e) => {
    setIsDragging(true);
    startY.current = e.clientY;
    startValue.current = value;
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  const handleMouseMove = (e) => {
    if (startY.current === null) return;

    const deltaY = startY.current - e.clientY;
    // Sensitivity: 100px drag = full range
    const range = max - min;
    const deltaValue = (deltaY / 100) * range;

    let newValue = startValue.current + deltaValue;
    // Clamp
    newValue = Math.min(Math.max(newValue, min), max);

    // Snap to step
    if (step) {
        newValue = Math.round(newValue / step) * step;
    }

    if (onChange) {
      onChange(newValue);
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
    startY.current = null;
    startValue.current = null;
    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('mouseup', handleMouseUp);
  };

  // Cleanup
  useEffect(() => {
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, []);

  return (
    <div className="knob-container" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', margin: '10px' }}>
      <div
        className="knob-control"
        onMouseDown={handleMouseDown}
        style={{ cursor: 'ns-resize', position: 'relative', width: size, height: size }}
        title={`${label}: ${value}`}
      >
        <svg width={size} height={size} style={{ transform: `rotate(${rotationOffset}deg)` }}>
          {/* Background Ring */}
          <circle
            cx={center}
            cy={center}
            r={radius}
            fill="none"
            stroke="var(--bg-panel)"
            strokeWidth={strokeWidth}
            strokeDasharray={`${arcLength} ${circumference}`}
            strokeLinecap="round"
          />
          {/* Track Ring (Darker) */}
          <circle
             cx={center}
             cy={center}
             r={radius}
             fill="none"
             stroke="#333"
             strokeWidth={strokeWidth}
             strokeDasharray={`${arcLength} ${circumference}`}
             strokeLinecap="round"
          />
          {/* Active Value Ring */}
          <circle
            cx={center}
            cy={center}
            r={radius}
            fill="none"
            stroke="var(--color-primary)"
            strokeWidth={strokeWidth}
            strokeDasharray={`${arcLength} ${circumference}`}
            strokeDashoffset={offset}
            strokeLinecap="round"
          />
        </svg>
        {/* Value Display (Centered) */}
        <div style={{
            position: 'absolute',
            top: 0, left: 0, width: '100%', height: '100%',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            pointerEvents: 'none',
            fontFamily: 'var(--font-tech)',
            fontSize: '0.8em',
            color: 'var(--color-primary)'
        }}>
            {Math.round(value)}
        </div>
      </div>
      <label style={{
          marginTop: '8px',
          fontFamily: 'var(--font-tech)',
          fontSize: '0.75rem',
          color: 'var(--text-muted)',
          textTransform: 'uppercase',
          letterSpacing: '0.05em'
      }}>
        {label}
      </label>
    </div>
  );
};

export default Knob;
